import type {
  InputOption,
  OpenToggleButtonInputProps
} from '@healthnz-prm/fhir-engine-renderer/types'
import { ChangeEvent } from 'react'
import { InputFieldset } from '../inputFieldset'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Input } from '@/components/ui/input'

interface EnrichedOpenToggleButtonInputProps
  extends OpenToggleButtonInputProps {
  handleChange(val: string): void
}

function determineValue(value: string | number | null, options: InputOption[]) {
  if (typeof value === 'number') return `index-${options[0].value}`

  if (typeof value === 'string') return 'other'

  return undefined
}

function BooleanInput({
  disabled,
  handleChange,
  openLabel = 'Other',
  options,
  value
}: EnrichedOpenToggleButtonInputProps) {
  return (
    <ToggleGroup
      disabled={disabled}
      value={determineValue(value, options)}
      onValueChange={handleChange}
      type='single'
      size='lg'
      variant='outline'
    >
      <ToggleGroupItem value={`index-${options[0].value}`}>
        {options[0].label}
      </ToggleGroupItem>
      <ToggleGroupItem value='other'>{openLabel}</ToggleGroupItem>
    </ToggleGroup>
  )
}

function ToggleInput(props: EnrichedOpenToggleButtonInputProps) {
  const { options } = props

  switch (options.length) {
    case 0:
      return <p>No options provided</p>
    case 1:
      return <BooleanInput {...props} />
    default:
      return <p>Too many options provided</p>
  }
}

export default function OpenToggleButtonInput(
  props: OpenToggleButtonInputProps
) {
  const { error, inputId, onChange, openLabel = 'Other', value } = props

  function handleChange(newValue: string) {
    if (newValue === 'other') {
      onChange('')
      return
    }

    if (!newValue.startsWith('index-')) return

    const parsedValue = Number.parseInt(newValue.slice(6), 10)

    onChange(parsedValue)
  }

  function handleOpenChange(event: ChangeEvent<HTMLInputElement>) {
    onChange(event.target.value)
  }

  return (
    <InputFieldset {...props}>
      <ToggleInput {...props} handleChange={handleChange} />

      {typeof value === 'string' && (
        <Input
          aria-invalid={!!error}
          aria-label={openLabel}
          name={`${inputId}-open`}
          onChange={handleOpenChange}
          type='text'
          value={value}
        />
      )}
    </InputFieldset>
  )
}
